import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Train, X } from 'lucide-react';
import {
  ActivePlayerFlag,
  ApiError,
  PLAYER_FLAG_KINDS,
  PlayerFlagKind,
  api,
} from '@/lib/api';

const KIND_PRIORITY: Record<PlayerFlagKind, number> = {
  red: 4,
  yellow: 3,
  green: 2,
  train_hint: 1,
};

const KIND_LABEL: Record<PlayerFlagKind, string> = {
  red: 'Red card',
  yellow: 'Yellow card',
  green: 'Should be picked',
  train_hint: 'Train hint',
};

function KindIcon({
  kind,
  size = 14,
  doubled = false,
}: {
  kind: PlayerFlagKind;
  size?: number;
  doubled?: boolean;
}) {
  if (kind === 'train_hint') {
    return <Train size={size} className="text-sky-300" />;
  }
  // Two yellow cards escalate to red-and-yellow.
  if (kind === 'yellow' && doubled) {
    const w = Math.round(size * 0.72);
    return (
      <span
        className="inline-flex overflow-hidden rounded-sm"
        style={{ width: w, height: size }}
        aria-label="Two yellow cards — counts as red"
      >
        <span className="bg-yellow-400" style={{ width: w / 2, height: size }} />
        <span className="bg-red-500" style={{ width: w / 2, height: size }} />
      </span>
    );
  }
  const bg =
    kind === 'red'
      ? 'bg-red-500'
      : kind === 'yellow'
      ? 'bg-yellow-400'
      : 'bg-emerald-500';
  return (
    <span
      className={`inline-block rounded-sm ${bg}`}
      style={{ width: Math.round(size * 0.72), height: size }}
    />
  );
}

function formatExpiry(iso: string | null): string {
  if (!iso) return 'no expiry';
  const t = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.round((t - now) / (24 * 60 * 60 * 1000));
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires tomorrow';
  return `expires in ${days} days`;
}

export function highestFlag(flags: ActivePlayerFlag[]): ActivePlayerFlag | null {
  if (!flags?.length) return null;
  return [...flags].sort(
    (a, b) => KIND_PRIORITY[b.kind] - KIND_PRIORITY[a.kind],
  )[0];
}

type Props = {
  playerId: number;
  flags: ActivePlayerFlag[];
  editable?: boolean;
  size?: number;
};

export function PlayerFlagChip({ playerId, flags, editable = false, size = 14 }: Props) {
  const [open, setOpen] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const chipRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const highest = useMemo(() => highestFlag(flags), [flags]);
  const yellowCount = useMemo(
    () => flags.filter((f) => f.kind === 'yellow').length,
    [flags],
  );
  const doubledYellow = yellowCount >= 2 && highest?.kind === 'yellow';

  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      const t = ev.target as Node;
      if (chipRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!highest && !editable) return null;

  if (!highest) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowIssue(true)}
          className="opacity-40 hover:opacity-100"
          title="Issue flag"
        >
          <span
            className="inline-block rounded-sm border border-slate-500"
            style={{ width: Math.round(size * 0.72), height: size }}
          />
        </button>
        {showIssue && (
          <IssueFlagDialog playerId={playerId} onClose={() => setShowIssue(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-0.5 align-middle"
        title={
          doubledYellow
            ? '2 yellow cards — counts as red'
            : KIND_LABEL[highest.kind]
        }
      >
        <KindIcon kind={highest.kind} size={size} doubled={doubledYellow} />
        {flags.length > 1 && !doubledYellow && (
          <span className="text-[10px] leading-none text-slate-400">+{flags.length - 1}</span>
        )}
        {doubledYellow && flags.length > 2 && (
          <span className="text-[10px] leading-none text-slate-400">+{flags.length - 2}</span>
        )}
      </button>
      {open && (
        <div className="relative inline-block">
          <div
            ref={popRef}
            className="absolute z-50 mt-1 w-72 rounded-lg border border-slate-700 bg-slate-900 p-3 text-left text-sm shadow-xl"
            style={{ top: 0, left: 0 }}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-slate-400">
                Active flags
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-200"
              >
                <X size={14} />
              </button>
            </div>
            <ul className="space-y-2">
              {flags
                .slice()
                .sort((a, b) => KIND_PRIORITY[b.kind] - KIND_PRIORITY[a.kind])
                .map((f) => (
                  <FlagRow
                    key={f.id}
                    flag={f}
                    playerId={playerId}
                    editable={editable}
                  />
                ))}
            </ul>
            {editable && (
              <button
                onClick={() => {
                  setOpen(false);
                  setShowIssue(true);
                }}
                className="mt-3 w-full rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Issue another…
              </button>
            )}
          </div>
        </div>
      )}
      {showIssue && (
        <IssueFlagDialog playerId={playerId} onClose={() => setShowIssue(false)} />
      )}
    </>
  );
}

function FlagRow({
  flag,
  playerId,
  editable,
}: {
  flag: ActivePlayerFlag;
  playerId: number;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => api.revokePlayerFlag(playerId, flag.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', playerId] });
      qc.invalidateQueries({ queryKey: ['playerFlags', playerId] });
      qc.invalidateQueries({ queryKey: ['activeFlags'] });
    },
  });
  return (
    <li className="rounded border border-slate-800 bg-slate-950/40 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <KindIcon kind={flag.kind} />
          <span className="text-sm">{KIND_LABEL[flag.kind]}</span>
        </div>
        <div className="text-xs text-slate-500">{formatExpiry(flag.expiresAt)}</div>
      </div>
      {flag.note && (
        <div className="mt-1 whitespace-pre-wrap text-xs text-slate-300">{flag.note}</div>
      )}
      {editable && (
        <div className="mt-1 flex justify-end">
          <button
            onClick={() => revoke.mutate()}
            disabled={revoke.isPending}
            className="text-[11px] text-slate-500 hover:text-red-400"
          >
            Revoke
          </button>
        </div>
      )}
    </li>
  );
}

export function IssueFlagDialog({
  playerId,
  onClose,
}: {
  playerId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<PlayerFlagKind>('yellow');
  const [note, setNote] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>(''); // YYYY-MM-DD or ''
  const [error, setError] = useState<string | null>(null);

  const noteRequired = kind !== 'train_hint';
  const expiryLocked = kind === 'green';

  const create = useMutation({
    mutationFn: () => {
      const body: {
        kind: PlayerFlagKind;
        note?: string | null;
        expiresAt?: string | null;
      } = { kind };
      const trimmedNote = note.trim();
      if (trimmedNote) body.note = trimmedNote;
      if (!expiryLocked && expiresAt) {
        // interpret input as end-of-day local
        const d = new Date(`${expiresAt}T23:59:59`);
        body.expiresAt = d.toISOString();
      }
      return api.createPlayerFlag(playerId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', playerId] });
      qc.invalidateQueries({ queryKey: ['playerFlags', playerId] });
      qc.invalidateQueries({ queryKey: ['activeFlags'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (noteRequired && !note.trim()) {
            setError('Note is required');
            return;
          }
          create.mutate();
        }}
        className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-4 shadow-xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-medium">Issue flag</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
              Kind
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PLAYER_FLAG_KINDS.map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setKind(k)}
                  className={`flex items-center gap-2 rounded border px-2 py-1.5 text-sm ${
                    kind === k
                      ? 'border-sky-500 bg-sky-500/10 text-sky-200'
                      : 'border-slate-700 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <KindIcon kind={k} />
                  {KIND_LABEL[k]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
              Note {noteRequired && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
              placeholder={
                kind === 'red'
                  ? 'Reason for red card'
                  : kind === 'yellow'
                  ? 'Reason for yellow card'
                  : kind === 'green'
                  ? 'Why should this player be picked?'
                  : 'Optional context for the train hint'
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
              Expires
            </label>
            {expiryLocked ? (
              <div className="text-sm text-slate-400">
                Fixed to 7 days for green flags.
              </div>
            ) : (
              <>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none [color-scheme:dark]"
                />
                <div className="mt-1 text-xs text-slate-500">
                  {kind === 'train_hint'
                    ? 'Blank = default 14 days.'
                    : 'Blank = no expiry (open-ended).'}
                </div>
              </>
            )}
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-500 disabled:opacity-50"
          >
            Issue
          </button>
        </div>
      </form>
    </div>
  );
}
